import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import { FaArrowLeft, FaCheckCircle, FaCog, FaClipboardCheck, FaClipboardList, FaTimes, FaExclamationTriangle, FaCamera, FaChevronDown, FaInfoCircle, FaPencilAlt } from 'react-icons/fa';
import { fetchEquipmentScopes } from './api/equipmentScopes';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
}

async function compressImage(file, maxWidth = 1200, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve({ name: file.name, dataUrl });
      };
      img.onerror = reject; img.src = e.target.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}

// ── Image Lightbox ───────────────────────────────────────────────
function ImageLightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: '1rem',
    }}>
      <img src={src} alt="" style={{ maxWidth: '95%', maxHeight: '90vh', borderRadius: 8, objectFit: 'contain' }} />
    </div>
  );
}

// A short question and the detail underneath it, rather than one long sentence
// a tech has to read twice on a phone. The ids are what answers are stored
// against and are unchanged, so checklists filled out under the old wording
// still read back correctly.
const CHECKLIST_QUESTIONS = [
  { id: 'visual_inspection', label: 'Good physical condition?', hint: 'No visible damage, leaks, or corrosion' },
  { id: 'operational_check', label: 'Operating normally?', hint: 'No unusual noise, vibration, or odor' },
  { id: 'filters_strainers', label: 'Filters and strainers serviced?', hint: 'Checked, cleaned, or replaced as needed' },
  { id: 'belts_lubrication', label: 'Belts and lubrication checked?', hint: 'Where applicable to this equipment' },
  { id: 'cleanliness', label: 'Area clean and clear?', hint: 'Equipment and surroundings free of debris' },
];

// Selection is a flat fill rather than a ring. The fill has to carry the state
// on its own, so these are a step stronger than a hover tint would be — a tech
// is reading this on a phone in a mechanical room.
const ANSWER_OPTIONS = [
  { val: 'yes', label: '✓ Yes', tint: '#dcfce7', text: '#047857' },
  { val: 'no', label: '✕ No', tint: '#fee2e2', text: '#b91c1c' },
  { val: 'na', label: '— N/A', tint: '#e5e7eb', text: '#374151' },
];

function Toast({ message, visible }) {
  return (
    <div style={{
      position: 'fixed', bottom: '2rem', left: '50%', transform: `translateX(-50%) translateY(${visible ? '0' : '1rem'})`,
      background: '#065f46', color: '#fff', padding: '0.85rem 1.5rem', borderRadius: '12px',
      fontWeight: 600, fontSize: '1.05rem', boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      display: 'flex', alignItems: 'center', gap: '0.5rem', zIndex: 10000,
      opacity: visible ? 1 : 0, transition: 'opacity 0.3s, transform 0.3s', pointerEvents: 'none',
    }}>
      <FaCheckCircle /> {message}
    </div>
  );
}

// ── Office instructions for the tech ─────────────────────────────
// The office writes what it wants done on this visit; the tech reads it before
// starting. Internal — stored apart from the visit's details field, which is
// the writeup that goes out to the manager. Nothing shows until something is
// written, so a visit with no special instructions stays uncluttered.
function VisitInstructions({ visitId, value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || ''); }, [value]);

  const save = async () => {
    const text = draft.trim();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/mechanical/tech/visit/${encodeURIComponent(visitId)}/instructions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions: text }),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      onSaved(text);
      setEditing(false);
    } catch (e) {
      console.error('[VisitInstructions] Save failed:', e);
      alert('Could not save instructions. Please try again.');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div style={{ marginTop: '0.85rem' }}>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#92400e', marginBottom: '0.35rem' }}>
          Instructions for tech
        </label>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          rows={4}
          placeholder="e.g. Tenant complained about noise from MAU-1 — check the bearings while you're up there. Caretaker has the roof key."
          style={{
            width: '100%', padding: '0.7rem 0.85rem', borderRadius: '10px', border: '1px solid #fcd34d',
            background: '#fffbeb', fontSize: '0.95rem', lineHeight: 1.5, color: '#111827',
            outline: 'none', resize: 'vertical', fontFamily: 'inherit',
          }}
        />
        <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '0.3rem' }}>
          Internal only — never shown on the manager's dashboard.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
          <button onClick={save} disabled={saving}
            style={{ padding: '0.5rem 1.1rem', borderRadius: '8px', border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '0.9rem', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => { setDraft(value || ''); setEditing(false); }} disabled={saving}
            style={{ padding: '0.5rem 1.1rem', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!value) {
    return (
      <button onClick={() => setEditing(true)}
        style={{
          marginTop: '0.75rem', padding: '0.4rem 0.75rem', borderRadius: '8px',
          border: '1px dashed #d1d5db', background: 'none', color: '#6b7280',
          fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        }}>
        <FaPencilAlt size={11} /> Add instructions for tech
      </button>
    );
  }

  return (
    <div style={{ marginTop: '0.85rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '0.75rem 0.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          Instructions for tech
        </span>
        <button onClick={() => setEditing(true)} title="Edit instructions"
          style={{ background: 'none', border: 'none', padding: '0.15rem 0.3rem', color: '#b45309', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', fontWeight: 600 }}>
          <FaPencilAlt size={11} /> Edit
        </button>
      </div>
      <div style={{ fontSize: '0.95rem', lineHeight: 1.55, color: '#78350f', whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  );
}

// ── Equipment Details Panel ──────────────────────────────────────
function EquipmentDetailsPanel({ equipment, onImageClick }) {
  const [expanded, setExpanded] = useState(false);
  if (!equipment) return null;

  const hasDetails = equipment.model || equipment.details || equipment.purpose || equipment.manufactureYear || equipment.tag || (equipment.additionalPhotos && equipment.additionalPhotos.length > 0);
  if (!hasDetails && !equipment.location) return null;

  const rows = [
    { label: 'Location', value: equipment.location },
    { label: 'Model', value: equipment.model },
    { label: 'Tag / Asset ID', value: equipment.tag },
    { label: 'Year', value: equipment.manufactureYear },
    { label: 'Purpose', value: equipment.purpose },
  ].filter((r) => r.value);

  return (
    <div style={{ marginBottom: '0.6rem', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', background: expanded ? '#fff' : '#f9fafb' }}>
      <button type="button" onClick={() => setExpanded((e) => !e)}
        style={{ width: '100%', padding: '0.75rem 1rem', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
          <FaInfoCircle style={{ color: '#6b7280', fontSize: '0.9rem' }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#374151' }}>Equipment details</span>
        </div>
        <FaChevronDown style={{ color: '#9ca3af', fontSize: '0.8rem', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {expanded && (
        <div style={{ padding: '0 1rem 1rem' }}>
          {rows.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: equipment.details ? '0.75rem' : 0 }}>
              {rows.map((r) => (
                <div key={r.label}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.15rem' }}>{r.label}</div>
                  <div style={{ fontSize: '0.95rem', color: '#111827' }}>{r.value}</div>
                </div>
              ))}
            </div>
          )}
          {equipment.details && (
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.15rem' }}>Details</div>
              <div style={{ fontSize: '0.95rem', color: '#111827', lineHeight: 1.5 }}>{equipment.details}</div>
            </div>
          )}
          {equipment.additionalPhotos && equipment.additionalPhotos.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Name Plates / Photos</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {equipment.additionalPhotos.map((url, i) => (
                  <img key={i} src={url} alt="" onClick={() => onImageClick(url)}
                    style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', cursor: 'zoom-in', border: '1px solid #e5e7eb' }} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Scope of Work Panel ──────────────────────────────────────────
// Written once per category by the office, so the scope for "Boiler" is the
// same on all five of a building's boilers. Collapsed by default and styled
// like the details panel above it: it's a reference a tech opens mid-checklist
// to check what the visit is meant to cover, not something to scroll past on
// every piece of equipment.
export function EquipmentScopePanel({ scope, category }) {
  const [expanded, setExpanded] = useState(false);
  if (!scope || !scope.trim()) return null;

  return (
    <div style={{ marginBottom: '1.15rem', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', background: expanded ? '#fff' : '#f9fafb' }}>
      <button type="button" onClick={() => setExpanded((e) => !e)}
        style={{ width: '100%', padding: '0.75rem 1rem', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', minWidth: 0 }}>
          <FaClipboardList style={{ color: '#6b7280', fontSize: '0.9rem', flexShrink: 0 }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#374151' }}>Scope of work</span>
          {category && <span style={{ fontSize: '0.8rem', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {category}</span>}
        </div>
        <FaChevronDown style={{ color: '#9ca3af', fontSize: '0.8rem', flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {expanded && (
        <div style={{ padding: '0 1rem 1rem' }}>
          {/* pre-wrap, not a paragraph: these are written with numbered steps
              and hanging indents, and the layout carries meaning. */}
          <div style={{
            fontSize: '0.92rem', color: '#111827', lineHeight: 1.55, whiteSpace: 'pre-wrap',
            background: '#f9fafb', borderRadius: 8, padding: '0.75rem', overflowWrap: 'break-word',
          }}>
            {scope}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Checklist Modal ──────────────────────────────────────────────
export function ChecklistModal({ open, equipment, savedChecklist, equipmentIssues, scope, onSubmit, onClose, onLogIssue, onEditIssue, onSaveDraft, submitting }) {
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [questionsExpanded, setQuestionsExpanded] = useState(true);
  const [extrasExpanded, setExtrasExpanded] = useState(true);
  const photoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (savedChecklist) {
      setAnswers(savedChecklist.answers || {});
      setNotes(savedChecklist.notes || '');
      setPhotos((savedChecklist.photoUrls || []).filter(Boolean).map((url) => ({ type: 'existing', url })));
    } else {
      setAnswers({}); setNotes(''); setPhotos([]);
    }
    setQuestionsExpanded(true);
    setExtrasExpanded(true);
  }, [open, equipment, savedChecklist]);

  const handlePhotos = useCallback(async (fileList) => {
    for (const file of fileList) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const c = await compressImage(file);
        setPhotos((prev) => [...prev, { type: 'new', name: c.name, dataUrl: c.dataUrl }]);
      } catch (e) { console.error(e); }
    }
  }, []);

  if (!open || !equipment) return null;

  const answeredCount = CHECKLIST_QUESTIONS.filter((q) => answers[q.id] !== undefined).length;
  const allAnswered = answeredCount === CHECKLIST_QUESTIONS.length;
  const remaining = CHECKLIST_QUESTIONS.length - answeredCount;
  const hasAnyNo = Object.values(answers).some((v) => v === 'no');
  const isEdit = !!savedChecklist;
  const extraCount = (notes.trim() ? 1 : 0) + photos.length;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', overflowY: 'auto', padding: '2rem 1rem' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: 520, padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flex: 1, minWidth: 0 }}>
            {equipment.photoUrl ? (
              <img src={equipment.photoUrl} alt="" onClick={(e) => { e.stopPropagation(); setLightboxSrc(equipment.photoUrl); }}
                style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', flexShrink: 0, background: '#f3f4f6', cursor: 'zoom-in' }} />
            ) : (
              <div style={{ width: 56, height: 56, borderRadius: 12, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FaCog style={{ fontSize: '1.5rem', color: '#9ca3af' }} />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{equipment.name}</h2>
              {(equipment.category || equipment.location) && (
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.15rem' }}>
                  {[equipment.category, equipment.location].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </div>
          <button aria-label="Close checklist" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: '#6b7280', padding: '0.25rem', flexShrink: 0, marginLeft: '0.5rem' }}><FaTimes /></button>
        </div>

        {/* Equipment Details (collapsible) */}
        <EquipmentDetailsPanel equipment={equipment} onImageClick={setLightboxSrc} />

        {/* Scope of Work for this equipment's category (collapsible) */}
        <EquipmentScopePanel scope={scope} category={equipment.category} />

        {/* Notes and photos — above the checklist because a tech writes what
            they saw as they work, and answering the last question shouldn't
            mean scrolling back past everything to type it. Grouped into one
            collapsible block so it can be folded away once filled in. */}
        <div style={{ marginBottom: '1.15rem', border: '1px dashed #d1d5db', borderRadius: '12px', background: '#fafafa', overflow: 'hidden' }}>
          <button type="button" aria-expanded={extrasExpanded} onClick={() => setExtrasExpanded((e) => !e)}
            style={{ width: '100%', padding: '0.8rem 0.9rem', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
            <FaCamera style={{ color: '#9ca3af', fontSize: '1rem', flexShrink: 0 }} />
            <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#374151', flex: 1, textAlign: 'left' }}>Notes and photos</span>
            <span style={{ fontSize: '0.8rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
              {extraCount > 0 ? `${photos.length} photo${photos.length === 1 ? '' : 's'}${notes.trim() ? ' · note added' : ''}` : 'Optional'}
            </span>
            <FaChevronDown style={{ color: '#9ca3af', fontSize: '0.75rem', flexShrink: 0, transition: 'transform 0.2s', transform: extrasExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>

          {extrasExpanded && (
            <div style={{ padding: '0 0.9rem 0.9rem' }}>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any observations, comments, or follow-ups..."
                rows={2} style={{ width: '100%', padding: '0.7rem', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '0.95rem', fontFamily: 'inherit', outline: 'none', resize: 'vertical', minHeight: 60, background: '#fff' }} />

              <div onClick={() => photoRef.current?.click()}
                style={{ marginTop: '0.6rem', border: '1px dashed #d1d5db', borderRadius: '10px', padding: '0.8rem', textAlign: 'center', cursor: 'pointer', background: '#fff' }}>
                <FaCamera style={{ fontSize: '1.2rem', color: '#9ca3af', marginBottom: '0.2rem' }} />
                <div style={{ fontSize: '0.92rem', color: '#6b7280' }}>Tap to add photos</div>
              </div>
              <input ref={photoRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { handlePhotos(Array.from(e.target.files)); e.target.value = ''; }} />

              {photos.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.6rem' }}>
                  {photos.map((p, i) => (
                    <div key={i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: p.type === 'existing' ? '2px solid #3b82f6' : '2px solid #16a34a' }}>
                      <img src={p.type === 'existing' ? p.url : p.dataUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button type="button" aria-label="Remove photo" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                        style={{ position: 'absolute', top: 1, right: 1, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(220,38,38,0.9)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Checklist section */}
        <div style={{ marginBottom: '1.25rem' }}>
          {/* Wraps rather than shrinks: the title, the count and "Mark all Yes"
              are all unbreakable, and together they are wider than a phone. */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.75rem' }}>
            {/* The heading doubles as the collapse control, so the count lives
                in one place instead of being repeated on a separate toggle row.
                "Mark all Yes" sits outside it — nesting buttons is invalid. */}
            <button type="button" aria-expanded={questionsExpanded} onClick={() => setQuestionsExpanded((e) => !e)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, textAlign: 'left' }}>
              <span style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>Inspection checklist</span>
              {allAnswered && <FaCheckCircle style={{ color: '#10b981', fontSize: '0.8rem', flexShrink: 0 }} />}
              <FaChevronDown style={{ color: '#9ca3af', fontSize: '0.65rem', flexShrink: 0, transition: 'transform 0.2s', transform: questionsExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>
            <span style={{ fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
              {answeredCount} of {CHECKLIST_QUESTIONS.length} answered
            </span>
            <button type="button"
              onClick={() => {
                const allYes = CHECKLIST_QUESTIONS.every((q) => answers[q.id] === 'yes');
                if (allYes) {
                  setAnswers({});
                } else {
                  const a = {};
                  CHECKLIST_QUESTIONS.forEach((q) => { a[q.id] = 'yes'; });
                  setAnswers(a);
                }
              }}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                fontSize: '0.85rem', fontWeight: 600, color: '#2563eb',
              }}>
              Mark all Yes
            </button>
          </div>

          {questionsExpanded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {CHECKLIST_QUESTIONS.map((q, qIdx) => {
                const answer = answers[q.id];
                return (
                  <div key={q.id} style={{
                    background: '#fff', borderRadius: '12px', padding: '0.85rem 0.9rem',
                    border: answer === 'no' ? '1px solid #fecaca' : '1px solid #e5e7eb',
                  }}>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600, color: '#111827', lineHeight: 1.35 }}>{qIdx + 1}. {q.label}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.1rem', marginBottom: '0.6rem', lineHeight: 1.4 }}>{q.hint}</div>

                    {/* One segmented control rather than three loose buttons:
                        the three answers are a single choice. */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                        {ANSWER_OPTIONS.map(({ val, label, tint, text }, i) => {
                        const sel = answer === val;
                        return (
                          <button key={val} type="button" aria-pressed={sel}
                            onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: val }))}
                            style={{
                              padding: '0.6rem 0.25rem', cursor: 'pointer', textAlign: 'center',
                              fontWeight: sel ? 700 : 500, fontSize: '0.92rem',
                              border: 'none', borderLeft: i > 0 ? '1px solid #e5e7eb' : 'none',
                              // The app's global button styles round every
                              // button, which turned the selected segment into a
                              // floating pill inside its own cell. Squared off,
                              // the fill meets the dividers and reads as one
                              // control; the container's radius still rounds the
                              // two outer ends.
                              borderRadius: 0,
                              background: sel ? tint : '#fff', color: sel ? text : '#4b5563',
                              transition: 'background 0.15s, color 0.15s',
                            }}>{label}</button>
                        );
                      })}
                    </div>

                    {/* Reporting offered right where the problem was found, so a
                        tech doesn't have to remember which question prompted it.
                        The same action is also available for the whole piece of
                        equipment below, for anything the questions don't cover. */}
                    {answer === 'no' && (
                      <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginTop: '0.65rem',
                        padding: '0.7rem 0.75rem', borderRadius: '10px', background: '#fffbeb', border: '1px solid #fde68a',
                      }}>
                        <FaExclamationTriangle style={{ color: '#d97706', fontSize: '0.95rem', flexShrink: 0, marginTop: '0.1rem' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#92400e' }}>Needs attention</div>
                          <div style={{ fontSize: '0.82rem', color: '#a16207', lineHeight: 1.35 }}>Add details so the team can follow up.</div>
                        </div>
                        <button type="button"
                          onClick={async () => { await onSaveDraft(equipment.id, answers, notes); onLogIssue(equipment); }}
                          style={{
                            flexShrink: 0, alignSelf: 'center', padding: '0.45rem 0.7rem', borderRadius: 8, cursor: 'pointer',
                            border: '1px solid #d97706', background: '#fff', color: '#92400e',
                            fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap',
                          }}>
                          Report issue →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Issues section — always available. Plenty of what a tech finds on
            site has nothing to do with the five checklist questions, so this
            can't be gated behind a No answer or an existing issue. */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: '0.92rem', color: '#374151', marginBottom: '0.45rem' }}>Issues</label>
          {equipmentIssues && equipmentIssues.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.65rem' }}>
              {equipmentIssues.map((iss) => (
                <div key={iss.id} onClick={() => onEditIssue(iss)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.55rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
                    background: '#fef2f2', border: '1px solid #fecaca', transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                    <FaExclamationTriangle style={{ color: '#dc2626', fontSize: '0.85rem', flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, color: '#991b1b', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{iss.name}</span>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', flexShrink: 0, marginLeft: '0.5rem' }}>Edit →</span>
                </div>
              ))}
            </div>
          )}
          <button onClick={async () => { await onSaveDraft(equipment.id, answers, notes); onLogIssue(equipment); }} type="button"
            style={{
              width: '100%', padding: '0.7rem', borderRadius: '10px',
              border: hasAnyNo ? '2px solid #dc2626' : '2px solid #f59e0b',
              background: hasAnyNo ? '#fef2f2' : '#fffbeb',
              fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
              color: hasAnyNo ? '#dc2626' : '#92400e',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              animation: hasAnyNo ? 'pulse-border 1.5s infinite' : 'none',
            }}>
            <FaExclamationTriangle /> {hasAnyNo ? 'Issue Detected — Report Issue' : 'Report New Issue'}
          </button>
          {hasAnyNo && <style>{`@keyframes pulse-border { 0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.3); } 50% { box-shadow: 0 0 0 6px rgba(220,38,38,0); } }`}</style>}
        </div>

        {/* Submit */}
        <button
          onClick={() => onSubmit(equipment.id, answers, notes, photos)}
          disabled={!allAnswered || submitting}
          style={{
            width: '100%', padding: '0.9rem', borderRadius: '10px', border: 'none',
            fontWeight: 700, fontSize: '1.05rem',
            cursor: (!allAnswered || submitting) ? 'not-allowed' : 'pointer',
            background: (!allAnswered || submitting) ? '#d1d5db' : '#2563eb',
            color: '#fff', opacity: submitting ? 0.7 : 1,
          }}>
          {submitting ? 'Saving...' : (isEdit ? 'Update inspection' : 'Save inspection')}
        </button>
        {/* Says why the button is dead rather than leaving a tech tapping it. */}
        {remaining > 0 && (
          <div style={{ textAlign: 'center', fontSize: '0.82rem', color: '#9ca3af', marginTop: '0.5rem' }}>
            {remaining} question{remaining === 1 ? '' : 's'} remaining
          </div>
        )}
      </div>
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────
export default function MechVisitChecklist() {
  const { buildingId, visitId } = useParams();
  const navigate = useNavigate();
  const [buildingData, setBuildingData] = useState(null);
  const [completedIds, setCompletedIds] = useState(new Set());
  const [checklists, setChecklists] = useState({});
  const [visitIssueCountByEquipment, setVisitIssueCountByEquipment] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [checklistEquipment, setChecklistEquipment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '' });
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [scopes, setScopes] = useState({});

  // Loaded alongside the page rather than per checklist: it's one small map for
  // the whole building, and a tech opening equipment on site shouldn't wait on a
  // request to see what the visit covers.
  useEffect(() => {
    let cancelled = false;
    fetchEquipmentScopes()
      .then((data) => { if (!cancelled) setScopes(data.scopes || {}); })
      .catch((e) => console.error('[MechVisitChecklist] Scopes unavailable:', e));
    return () => { cancelled = true; };
  }, []);

  const showToast = useCallback((msg) => {
    setToast({ visible: true, message: msg });
    setTimeout(() => setToast({ visible: false, message: '' }), 2500);
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [buildingRes, visitEqRes, checklistRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/mechanical/tech/building/${encodeURIComponent(buildingId)}/detail`),
        fetch(`${API_BASE_URL}/api/mechanical/tech/visit/${encodeURIComponent(visitId)}/equipment`),
        fetch(`${API_BASE_URL}/api/mechanical/tech/visit/${encodeURIComponent(visitId)}/checklists`),
      ]);
      if (!buildingRes.ok) throw new Error('Failed to load building');
      const bData = await buildingRes.json();
      setBuildingData(bData);
      const counts = {};
      (bData.issues || []).filter((i) => i.visitId === visitId && i.equipmentId).forEach((i) => {
        counts[i.equipmentId] = (counts[i.equipmentId] || 0) + 1;
      });
      setVisitIssueCountByEquipment(counts);
      if (visitEqRes.ok) {
        const veData = await visitEqRes.json();
        setCompletedIds(new Set(veData.equipmentIds || []));
      }
      if (checklistRes.ok) {
        const clData = await checklistRes.json();
        setChecklists(clData.checklists || {});
      }
      setError(null);
    } catch (e) {
      console.error('[MechVisitChecklist] Error:', e);
      setError('Failed to load data.');
    } finally { setIsLoading(false); }
  }, [buildingId, visitId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChecklistSubmit = async (equipmentId, answers, notes, photos) => {
    setSubmitting(true);
    try {
      // Upload new photos to the visit first
      let allPhotoUrls = photos.filter((p) => p.type === 'existing').map((p) => p.url);
      const newPhotos = photos.filter((p) => p.type === 'new');
      if (newPhotos.length > 0) {
        const photoPayloads = newPhotos.map((p) => ({ name: p.name, type: 'image/jpeg', data: p.dataUrl.split(',')[1] }));
        try {
          const uploadRes = await fetch(`${API_BASE_URL}/api/mechanical/tech/visit/${encodeURIComponent(visitId)}/add-photos`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ photos: photoPayloads }),
          });
          if (uploadRes.ok) {
            const uploadData = await uploadRes.json();
            if (uploadData.urls) allPhotoUrls = [...allPhotoUrls, ...uploadData.urls];
          }
        } catch (_) { console.error('Photo upload failed (non-blocking)'); }
      }

      // Get technician from visit
      const visit = (buildingData?.visits || []).find((v) => v.id === visitId);

      // Save checklist to MechanicalChecklist table
      await fetch(`${API_BASE_URL}/api/mechanical/tech/checklist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitId, equipmentId, buildingId,
          technician: visit?.technician || '',
          answers, notes, photoUrls: allPhotoUrls,
        }),
      });

      // Attach equipment to visit relation (if not already)
      if (!completedIds.has(equipmentId)) {
        await fetch(`${API_BASE_URL}/api/mechanical/tech/visit/${encodeURIComponent(visitId)}/attach-equipment`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ equipmentId }),
        });
      }

      const eqName = (buildingData?.equipment || []).find((e) => e.id === equipmentId)?.name || 'Equipment';
      setCompletedIds((prev) => new Set([...prev, equipmentId]));
      setChecklists((prev) => ({ ...prev, [equipmentId]: { answers, notes, photoUrls: allPhotoUrls, completed: true } }));
      setChecklistEquipment(null);
      showToast(`${eqName} — checklist ${checklists[equipmentId] ? 'updated' : 'complete'}!`);
    } catch (e) {
      console.error('Save error:', e);
      alert('Failed to save checklist. Please try again.');
    } finally { setSubmitting(false); }
  };

  const handleSaveDraft = async (equipmentId, answers, notes) => {
    const visit = (buildingData?.visits || []).find((v) => v.id === visitId);
    try {
      await fetch(`${API_BASE_URL}/api/mechanical/tech/checklist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitId, equipmentId, buildingId,
          technician: visit?.technician || '',
          answers, notes, photoUrls: [],
          completed: false,
        }),
      });
      setChecklists((prev) => ({ ...prev, [equipmentId]: { answers, notes, photoUrls: [], completed: false } }));
    } catch (e) { console.error('Draft save failed:', e); }
  };

  // Patch the visit in place instead of reloading the building: the tech may
  // be partway through the checklist and a refetch would throw away the open
  // state underneath them.
  const handleInstructionsSaved = useCallback((text) => {
    setBuildingData((prev) => prev && ({
      ...prev,
      visits: (prev.visits || []).map((v) => (v.id === visitId ? { ...v, techInstructions: text } : v)),
    }));
  }, [visitId]);

  const handleLogIssue = (eq) => {
    setChecklistEquipment(null);
    navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}/issue?equipmentId=${encodeURIComponent(eq.id)}&visitId=${encodeURIComponent(visitId)}`);
  };

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4rem 1rem' }}>
      <Spinner animation="border" style={{ color: '#2563eb', marginBottom: '1rem' }}><span className="visually-hidden">Loading...</span></Spinner>
      <p style={{ color: '#6b7280', fontWeight: 500, fontSize: '1.1rem', margin: 0 }}>Loading...</p>
    </div>
  );

  if (error || !buildingData) return (
    <div style={{ padding: '2rem' }}>
      <button onClick={() => navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', fontWeight: 600, fontSize: '1rem', padding: 0, marginBottom: '1rem' }}>
        <FaArrowLeft /> Back
      </button>
      <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', color: '#991b1b', fontSize: '1rem' }}>{error || 'Something went wrong.'}</div>
    </div>
  );

  const { equipment: allEquipment = [], visits = [] } = buildingData;
  const visit = visits.find((v) => v.id === visitId);
  // The office picks which equipment a visit covers, so the checklist is that
  // list and not the building's full inventory. Visits booked before the
  // picker existed carry no plan, and those still show everything.
  const planned = new Set(visit?.plannedEquipmentIds || []);
  const equipment = planned.size > 0
    ? allEquipment.filter((eq) => planned.has(eq.id))
    : allEquipment;
  const complete = equipment.filter((eq) => completedIds.has(eq.id));
  const filtered = equipment.filter((eq) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (eq.name || '').toLowerCase().includes(q) || (eq.category || '').toLowerCase().includes(q) || (eq.location || '').toLowerCase().includes(q);
  });
  // Against the planned list, so finishing the eight items a visit actually
  // covers reads as done rather than as a quarter of the building.
  const progress = equipment.length > 0 ? Math.round((complete.length / equipment.length) * 100) : 0;

  return (
    <div style={{ padding: '1.5rem 1.25rem', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <button onClick={() => navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}`)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', fontWeight: 600, fontSize: '1rem', padding: 0, marginBottom: '1.25rem' }}>
          <FaArrowLeft /> Back to building
        </button>

        <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', margin: 0 }}>{visit?.name || 'Visit'}</h1>
          <div style={{ display: 'flex', gap: '1rem', fontSize: '1rem', color: '#6b7280', marginTop: '0.35rem', flexWrap: 'wrap' }}>
            {visit?.technician && <span>👤 {visit.technician}</span>}
            <span>📅 {formatDate(visit?.dateVisited)}</span>
          </div>
          {visit && (
            <VisitInstructions
              visitId={visitId}
              value={visit.techInstructions || ''}
              onSaved={handleInstructionsSaved}
            />
          )}
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.05rem', fontWeight: 600, color: '#111827' }}>Equipment Progress</span>
            <span style={{ fontSize: '1.05rem', fontWeight: 700, color: progress === 100 ? '#10b981' : '#2563eb' }}>{complete.length} / {equipment.length}</span>
          </div>
          <div style={{ width: '100%', height: 12, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: progress === 100 ? '#10b981' : 'linear-gradient(90deg, #3b82f6, #60a5fa)', borderRadius: 6, transition: 'width 0.3s ease' }} />
          </div>
        </div>

        {equipment.length > 5 && (
          <input type="text" placeholder="Search equipment..." value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '0.8rem 1rem', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '1rem', outline: 'none', marginBottom: '1rem', background: '#fff' }} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontSize: '1rem' }}>
              {search ? 'No equipment matches your search.' : 'No equipment found.'}
            </div>
          ) : (
            filtered.map((eq) => {
              const isDone = completedIds.has(eq.id);
              return (
                <div key={eq.id} onClick={() => setChecklistEquipment(eq)}
                  style={{
                    background: '#fff', borderRadius: '12px',
                    border: isDone ? '2px solid #bbf7d0' : '1px solid #e5e7eb',
                    padding: '1rem 1.15rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.85rem', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!isDone) e.currentTarget.style.borderColor = '#93c5fd'; }}
                  onMouseLeave={(e) => { if (!isDone) e.currentTarget.style.borderColor = isDone ? '#bbf7d0' : '#e5e7eb'; }}>
                  {eq.photoUrl ? (
                    <img src={eq.photoUrl} alt=""
                      onClick={(e) => { e.stopPropagation(); setLightboxSrc(eq.photoUrl); }}
                      style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0, background: '#f3f4f6', cursor: 'zoom-in' }}
                      onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                  ) : null}
                  <div style={{ width: 48, height: 48, borderRadius: 10, background: isDone ? '#f0fdf4' : '#f3f4f6', display: eq.photoUrl ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {isDone ? <FaCheckCircle style={{ fontSize: '1.5rem', color: '#10b981' }} /> : <FaCog style={{ fontSize: '1.3rem', color: '#9ca3af' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#111827', fontSize: '1.05rem' }}>{eq.name}</div>
                    <div style={{ fontSize: '0.9rem', color: '#6b7280', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.1rem' }}>
                      {eq.category && <span>{eq.category}</span>}
                      {eq.location && <span>📍 {eq.location}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem', flexShrink: 0 }}>
                    {isDone ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#10b981', fontWeight: 600, fontSize: '0.95rem' }}>
                        <FaCheckCircle /> Done
                      </div>
                    ) : checklists[eq.id] && !checklists[eq.id].completed ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.55rem 0.85rem', borderRadius: '10px', fontWeight: 600, fontSize: '0.9rem', background: '#f59e0b', color: '#fff' }}>
                        <FaClipboardCheck /> In Progress
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.55rem 0.85rem', borderRadius: '10px', fontWeight: 600, fontSize: '0.9rem', background: '#2563eb', color: '#fff' }}>
                        <FaClipboardCheck /> Checklist
                      </div>
                    )}
                    {visitIssueCountByEquipment[eq.id] > 0 && (
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#dc2626' }}>
                        {visitIssueCountByEquipment[eq.id]} Issue{visitIssueCountByEquipment[eq.id] > 1 ? 's' : ''} Reported
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {equipment.length > 0 && complete.length === equipment.length && (
          <button onClick={() => navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}`)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '1rem', borderRadius: '12px', border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: '1.15rem', marginTop: '1.5rem',
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', color: '#fff',
              boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
            }}>
            <FaCheckCircle /> All Equipment Complete — Back to Building
          </button>
        )}
      </div>

      <ChecklistModal
        open={!!checklistEquipment}
        equipment={checklistEquipment}
        savedChecklist={checklistEquipment ? checklists[checklistEquipment.id] || null : null}
        equipmentIssues={checklistEquipment ? (buildingData?.issues || []).filter((i) => i.visitId === visitId && i.equipmentId === checklistEquipment.id) : []}
        scope={checklistEquipment ? scopes[checklistEquipment.category] || '' : ''}
        onSubmit={handleChecklistSubmit}
        onClose={() => setChecklistEquipment(null)}
        onLogIssue={handleLogIssue}
        onEditIssue={(iss) => {
          setChecklistEquipment(null);
          navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}/issue?equipmentId=${encodeURIComponent(iss.equipmentId)}&visitId=${encodeURIComponent(visitId)}&issueId=${encodeURIComponent(iss.id)}`);
        }}
        onSaveDraft={handleSaveDraft}
        submitting={submitting}
      />

      <Toast message={toast.message} visible={toast.visible} />
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
}
